import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { CODING_TOPICS, topicName, type CodingTopicId, type TopicId } from "../../data/prep/topics";
import type { PrepLevel } from "../../data/prep/roleProfiles";
import { RATING_SCORE, type Rating, type Skills, type Weekday } from "../../utils/prepSkills";
import { waitingOn } from "../../utils/prepPlanner";
import { MINUTE_OPTIONS, WEEK, WEEKDAY_LABEL, formatMinutes, type PrepSettingsDoc } from "../../utils/prep";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { cn } from "../../lib/utils";
import BackLink from "../BackLink";

// Interview Prep settings, from the page's settings button: level and
// Premium as pills up top, where Caramel thinks the user is in each topic
// (in roadmap order, with what they said at setup to compare), and their week
// on the right. Every change saves as it's made.

type Answers = Omit<PrepSettingsDoc, "completedAt">;

/** One change from this page: only what changed, merged into the saved settings */
export type SettingsPatch = Partial<Omit<Answers, "dayMinutes">> & { dayMinutes?: Partial<Answers["dayMinutes"]> };

const LEVEL_LABEL: Record<PrepLevel, string> = {
  intern: "Internships",
  newgrad: "New grad",
  mid: "Mid-level",
  senior: "Senior",
};
const PREMIUM_LABEL = (premium: boolean) => (premium ? "Have LeetCode Premium" : "No LeetCode Premium");
const RATINGS: Rating[] = ["shaky", "okay", "solid"];
const RATING_LABEL: Record<Rating, string> = {
  shaky: "Shaky",
  okay: "Okay",
  solid: "Solid",
};
/** The roadmap's sections, in order */
const GROUPS = ["Basics", "Trees & graphs", "Techniques"] as const;

const COLOR: Record<Rating, { bar: string; text: string }> = {
  shaky: { bar: "#C27A5E", text: "text-[#A5492E]" },
  okay: { bar: "#D9A35E", text: "text-melt-toffee" },
  solid: { bar: "#7E9A72", text: "text-[#4F6B47]" },
};

/** The word for a score: halfway between the setup ratings */
function ratingOf(score: number): Rating {
  if (score < (RATING_SCORE.shaky + RATING_SCORE.okay) / 2) return "shaky";
  if (score < (RATING_SCORE.okay + RATING_SCORE.solid) / 2) return "okay";
  return "solid";
}

const PILL =
  "inline-flex h-9 items-center gap-2 rounded-[10px] border border-melt-line bg-melt-paper px-3 text-[13px] font-medium text-melt-text1 transition-colors hover:border-melt-text1/25 data-[state=open]:border-melt-text1/25";
const MENU = "w-auto border-melt-hair bg-melt-menu p-1.5 font-grotesk text-melt-text1 shadow-[0_20px_40px_-20px_rgba(92,52,23,.35)]";
const OPTION = (on: boolean) =>
  cn(
    "flex w-full items-center justify-between gap-6 rounded-[8px] px-2.5 py-1.5 text-left text-[13px] transition-colors hover:bg-melt-text1/[.05]",
    on ? "font-semibold text-melt-text1" : "text-melt-text2",
  );

/** A pill that opens a small menu */
function Pill({ label, children }: { label: ReactNode; children: (close: () => void) => ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger className={PILL}>
        {label}
        <ChevronDown size={14} strokeWidth={1.75} className="text-melt-text3" />
      </PopoverTrigger>
      <PopoverContent align="start" className={MENU}>
        {children(() => setOpen(false))}
      </PopoverContent>
    </Popover>
  );
}

/** The week chart's top: the most time a day can have */
const DAY_MAX = MINUTE_OPTIONS[MINUTE_OPTIONS.length - 1];
/** Faint lines across the chart */
const HOUR_LINES = [60, 120, 180, 240];

/** "1h", "1h30", "45m" */
const shortMinutes = (m: number) => formatMinutes(m).replace(" min", "m").replace(" h ", "h").replace(" h", "h");

/** The step nearest a height on the chart */
const nearestMinutes = (minutes: number) =>
  MINUTE_OPTIONS.reduce((best, m) => (Math.abs(m - minutes) < Math.abs(best - minutes) ? m : best));

/**
 * One day's bar. The whole column is the control: drag the bar up or down, or
 * click anywhere in it, and it snaps to the nearest step; arrow keys step too.
 * Saves when let go.
 */
function DayBar({ day, minutes, onChange }: { day: Weekday; minutes: number; onChange: (minutes: number) => void }) {
  /** Minutes under the pointer while dragging, before snapping */
  const [drag, setDrag] = useState<number | null>(null);
  const [pending, setPending] = useState<number | null>(null);
  useEffect(() => {
    if (pending === minutes) setPending(null);
  }, [pending, minutes]);

  const value = drag !== null ? nearestMinutes(drag) : pending ?? minutes;
  const height = drag ?? value;
  const at = (e: React.PointerEvent<HTMLDivElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    return Math.min(DAY_MAX, Math.max(0, ((box.bottom - e.clientY) / box.height) * DAY_MAX));
  };
  const commit = (next: number) => {
    setPending(next);
    if (next !== minutes) onChange(next);
  };
  const step = (by: number) => {
    const i = MINUTE_OPTIONS.indexOf(nearestMinutes(pending ?? minutes));
    commit(MINUTE_OPTIONS[Math.min(MINUTE_OPTIONS.length - 1, Math.max(0, i + by))]);
  };

  return (
    <div className="flex flex-col items-center gap-1.5">
      <div
        role="slider"
        tabIndex={0}
        aria-label={`Time on ${WEEKDAY_LABEL[day]}`}
        aria-orientation="vertical"
        aria-valuemin={0}
        aria-valuemax={DAY_MAX}
        aria-valuenow={value}
        aria-valuetext={value ? formatMinutes(value) : "Off"}
        className="group relative flex h-[180px] w-full cursor-ns-resize touch-none select-none items-end rounded-[8px] outline-none focus-visible:ring-2 focus-visible:ring-melt-caramel/40"
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          setDrag(at(e));
        }}
        onPointerMove={(e) => {
          if (drag !== null) setDrag(at(e));
        }}
        onPointerUp={(e) => {
          if (drag === null) return;
          setDrag(null);
          commit(nearestMinutes(at(e)));
        }}
        onPointerCancel={() => setDrag(null)}
        onKeyDown={(e) => {
          if (e.key === "ArrowUp" || e.key === "ArrowRight") step(1);
          else if (e.key === "ArrowDown" || e.key === "ArrowLeft") step(-1);
          else return;
          e.preventDefault();
        }}
      >
        <div
          className={cn(
            "relative w-full rounded-[7px] group-hover:brightness-[.97]",
            height ? "bg-[#D9A35E]" : "bg-melt-track"
          )}
          style={{
            height: height ? `${Math.max(6, (height / DAY_MAX) * 100)}%` : 4,
            transition: drag === null ? "height 180ms ease-out" : undefined,
          }}
        >
          {/* The grab edge */}
          {height > 0 && <i className="absolute inset-x-2 top-1 h-[3px] rounded-full bg-white/60 opacity-0 transition-opacity group-hover:opacity-100" />}
          {/* The time, riding on top of the bar */}
          <span
            className={cn(
              "absolute bottom-full left-1/2 mb-1 -translate-x-1/2 whitespace-nowrap font-plex text-[11px] leading-4 tabular-nums",
              value ? "text-melt-text1" : "text-melt-text3"
            )}
          >
            {value ? shortMinutes(value) : "Off"}
          </span>
        </div>
      </div>
      <span className="font-plex text-[10.5px] text-melt-text3">{WEEKDAY_LABEL[day].slice(0, 2)}</span>
    </div>
  );
}

/** The week as bars: drag one to set that day's time */
function WeekEditor({ days, onChange }: { days: Record<Weekday, number>; onChange: (days: Partial<Record<Weekday, number>>) => void }) {
  return (
    <div className="relative">
      {/* Hour lines, lined up with the bars' area (under the value labels, above the day names) */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[180px]" aria-hidden="true">
        {HOUR_LINES.map((m) => (
          <div key={m} className="absolute inset-x-0 border-t border-dashed border-melt-hair" style={{ bottom: `${(m / DAY_MAX) * 100}%` }}>
            <span className="absolute -top-[7px] right-0 bg-melt-paper pl-1 font-plex text-[9.5px] text-melt-text3/70">{m / 60}h</span>
          </div>
        ))}
      </div>
      <div className="relative grid grid-cols-7 gap-2 pr-6">
        {WEEK.map((day) => (
          <DayBar key={day} day={day} minutes={days[day]} onChange={(minutes) => onChange({ [day]: minutes })} />
        ))}
      </div>
    </div>
  );
}

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

/**
 * Where a score sits on the bar: shaky, okay and solid evenly spaced, solid at
 * the end. Scores run 0 to 1 but solid is 0.8, so a straight 0-to-1 bar would
 * never fill.
 */
const STOP: Record<Rating, number> = { shaky: 0.12, okay: 0.56, solid: 1 };
function onBar(score: number): number {
  const points: [number, number][] = [
    [0, 0],
    [RATING_SCORE.shaky, STOP.shaky],
    [RATING_SCORE.okay, STOP.okay],
    [RATING_SCORE.solid, STOP.solid],
  ];
  if (score >= RATING_SCORE.solid) return 1;
  const k = points.findIndex(([s]) => s > score);
  const [s0, x0] = points[k - 1];
  const [s1, x1] = points[k];
  return x0 + ((score - s0) / (s1 - s0)) * (x1 - x0);
}

/** The setup rating nearest a spot on the bar (0 to 1) */
const nearestRating = (at: number) => RATINGS.reduce((best, r) => (Math.abs(STOP[r] - at) < Math.abs(STOP[best] - at) ? r : best));

/** How long the knob takes to settle on a stop; the save waits for it */
const SNAP_MS = 200;

/**
 * One topic: the bar is where practice puts them, and the knob rides its end.
 * Drag the knob (or click the bar, or use the arrow keys) to say where they
 * really are. It follows the pointer, settles near shaky / okay / solid when let
 * go (practice keeps its gap from what they said), and saves once it has settled.
 */
function TopicRow({
  name,
  score,
  said,
  onRate,
  children,
}: {
  name: string;
  score: number;
  said: Rating;
  onRate: (rating: Rating) => void;
  /** A line under the bar: the topic waiting on others, or started early */
  children?: ReactNode;
}) {
  /** The knob's spot (0 to 1) while it's being dragged */
  const [drag, setDrag] = useState<number | null>(null);
  /** Picked, shown right away, until the saved rating comes back */
  const [pending, setPending] = useState<Rating | null>(null);
  const timer = useRef<number>();
  useEffect(() => {
    if (pending === said) setPending(null);
  }, [pending, said]);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  // Practice moves the bar from what they said; it keeps that gap as the rating changes
  const gap = onBar(score) - STOP[said];
  /** The rating that puts the knob nearest a spot on the bar */
  const ratingAt = (at: number) => nearestRating(at - gap);
  const rating = drag !== null ? ratingAt(drag) : pending ?? said;
  const knob = drag ?? clamp01(STOP[rating] + gap);
  const now = ratingOf(clamp01(score + RATING_SCORE[rating] - RATING_SCORE[said]));

  const spot = (e: React.PointerEvent<HTMLDivElement>) => {
    const box = (e.currentTarget.firstElementChild ?? e.currentTarget).getBoundingClientRect();
    return clamp01((e.clientX - box.left) / box.width);
  };
  const commit = (picked: Rating) => {
    setPending(picked);
    window.clearTimeout(timer.current);
    if (picked !== said) timer.current = window.setTimeout(() => onRate(picked), SNAP_MS);
  };
  const step = (by: number) => {
    const from = RATINGS.indexOf(pending ?? said);
    commit(RATINGS[Math.min(RATINGS.length - 1, Math.max(0, from + by))]);
  };
  const settle = drag === null ? { transition: `left ${SNAP_MS}ms ease-out, width ${SNAP_MS}ms ease-out, transform 150ms` } : undefined;

  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1.5 py-2.5 sm:grid-cols-[150px_minmax(0,1fr)_84px]">
      <span className="truncate text-[13.5px] text-melt-text1">{name}</span>
      <div
        role="slider"
        tabIndex={0}
        aria-label={`${name}: where you are`}
        aria-valuemin={0}
        aria-valuemax={RATINGS.length - 1}
        aria-valuenow={RATINGS.indexOf(rating)}
        aria-valuetext={RATING_LABEL[rating]}
        title={`You said ${said}. Drag to change.`}
        className="group relative col-span-2 row-start-2 -my-2 cursor-pointer touch-none select-none px-2 py-2 outline-none sm:col-span-1 sm:row-start-auto"
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          setDrag(spot(e));
        }}
        onPointerMove={(e) => {
          if (drag !== null) setDrag(spot(e));
        }}
        onPointerUp={(e) => {
          if (drag === null) return;
          setDrag(null);
          commit(ratingAt(spot(e)));
        }}
        onPointerCancel={() => setDrag(null)}
        onKeyDown={(e) => {
          if (e.key === "ArrowRight" || e.key === "ArrowUp") step(1);
          else if (e.key === "ArrowLeft" || e.key === "ArrowDown") step(-1);
          else return;
          e.preventDefault();
        }}
      >
        <div className="relative h-2 rounded-full bg-melt-track">
          <div className="h-full rounded-full" style={{ width: `${Math.max(4, knob * 100)}%`, background: COLOR[now].bar, ...settle }} />
          {RATINGS.map((r) => (
            <i
              key={r}
              className="absolute top-1/2 h-1 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-melt-text1/20"
              style={{ left: `${STOP[r] * 100}%` }}
            />
          ))}
          <i
            className={cn(
              "absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-melt-text1 shadow-[0_1px_3px_rgba(92,52,23,.35)] will-change-[left] group-focus-visible:ring-2 group-focus-visible:ring-melt-caramel/50",
              drag !== null ? "scale-110" : "group-hover:scale-110"
            )}
            style={{ left: `${knob * 100}%`, ...settle }}
          />
        </div>
      </div>
      <span className={cn("col-start-2 row-start-1 font-plex text-[11.5px] sm:col-start-auto sm:row-start-auto", COLOR[now].text)}>
        {now}
      </span>
      {children && <div className="col-span-2 sm:col-span-1 sm:col-start-2">{children}</div>}
    </li>
  );
}

export default function PrepSettings({
  settings,
  skills,
  onChange,
  onBack,
}: {
  settings: PrepSettingsDoc;
  /** Where the user is now: setup ratings plus practice */
  skills: Skills;
  /** Saves right away */
  onChange: (patch: SettingsPatch) => void;
  /** Back to the plan */
  onBack: () => void;
}) {
  const days = settings.dayMinutes;
  const total = WEEK.reduce((sum, d) => sum + days[d], 0);
  const said = (id: TopicId): Rating => settings.ratings[id] ?? "okay";
  const unlocked = settings.unlocked ?? [];
  const setUnlocked = (topic: CodingTopicId, on: boolean) =>
    onChange({ unlocked: on ? [...unlocked, topic] : unlocked.filter((t) => t !== topic) });
  /** Under a topic that waits on others: what for, and a way to start it anyway */
  const waitLine = (topic: CodingTopicId) => {
    const before = waitingOn(topic, skills);
    if (!before.length) return null;
    const names = before.map(topicName).join(" and ");
    const early = unlocked.includes(topic);
    return (
      <p className="text-[11.5px] leading-[1.45] text-melt-text3">
        {early ? `Started early, before ${names}` : `Waits until ${names} ${before.length > 1 ? "are" : "is"} solid`}
        {" · "}
        <button
          type="button"
          onClick={() => setUnlocked(topic, !early)}
          className="font-medium text-melt-caramel underline-offset-2 hover:underline"
        >
          {early ? "Wait instead" : "Start now"}
        </button>
      </p>
    );
  };

  return (
    <div className="mx-auto flex w-full max-w-[820px] flex-col gap-5 pb-8 font-grotesk text-melt-text1">
      <BackLink to="Interview Prep" onClick={onBack} className="-mb-2" />
      <div className="flex flex-wrap items-center gap-2">
        <Pill label={LEVEL_LABEL[settings.level]}>
          {(close) =>
            (Object.keys(LEVEL_LABEL) as PrepLevel[]).map((level) => (
              <button
                key={level}
                type="button"
                className={OPTION(level === settings.level)}
                onClick={() => {
                  onChange({ level });
                  close();
                }}
              >
                {LEVEL_LABEL[level]}
              </button>
            ))
          }
        </Pill>

        <Pill label={PREMIUM_LABEL(settings.leetcodePremium)}>
          {(close) => (
            <>
              {[true, false].map((premium) => (
                <button
                  key={String(premium)}
                  type="button"
                  className={OPTION(premium === settings.leetcodePremium)}
                  onClick={() => {
                    onChange({ leetcodePremium: premium });
                    close();
                  }}
                >
                  {PREMIUM_LABEL(premium)}
                </button>
              ))}
              <p className="max-w-[220px] px-2.5 pb-1 pt-1.5 text-[11.5px] leading-[1.45] text-melt-text3">
                Without it, problems only Premium can open are left out.
              </p>
            </>
          )}
        </Pill>

        <Pill label={settings.hideTopics ? "Hide topics" : "Show topics"}>
          {(close) =>
            [false, true].map((hide) => (
              <button
                key={String(hide)}
                type="button"
                className={OPTION(hide === Boolean(settings.hideTopics))}
                onClick={() => {
                  onChange({ hideTopics: hide });
                  close();
                }}
              >
                {hide ? "Hide topics" : "Show topics"}
              </button>
            ))
          }
        </Pill>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <section className="min-w-0 flex-1 rounded-2xl border border-melt-line bg-melt-paper p-5">
          <h2 className="text-[15px] font-semibold">Where Caramel thinks you are</h2>
          <p className="mt-1 text-[12.5px] leading-[1.5] text-melt-text2">
            This is what your plan works on. Think it’s wrong? Drag the dot to where you are.
          </p>
          <div className="scrollbar-thin mt-3 max-h-[440px] overflow-y-auto pr-2">
            {GROUPS.map((group) => (
              <div key={group} className="mt-3 first:mt-0">
                <span className="font-plex text-[10.5px] uppercase tracking-[.08em] text-melt-text3">{group}</span>
                <ul className="mt-1 divide-y divide-melt-hair">
                  {CODING_TOPICS.filter((t) => t.group === group).map((topic) => (
                    <TopicRow
                      key={topic.id}
                      name={topic.name}
                      score={skills[topic.id]}
                      said={said(topic.id)}
                      onRate={(rating) => onChange({ ratings: { [topic.id]: rating } })}
                    >
                      {waitLine(topic.id as CodingTopicId)}
                    </TopicRow>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full shrink-0 rounded-2xl border border-melt-line bg-melt-paper p-5 lg:w-[310px]">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-[15px] font-semibold">Your week</h2>
            <span className="font-plex text-[11px] text-melt-text3">{total ? `${formatMinutes(total)} a week` : "No time set"}</span>
          </div>
          <div className="mt-8">
            <WeekEditor days={days} onChange={(dayMinutes) => onChange({ dayMinutes })} />
          </div>
        </section>
      </div>
    </div>
  );
}
